<template>
    <div class="homiii-month-picker">

        <!-- Resumen de lo elegido / instrucción -->
        <div class="hmp-summary">
            <div class="hmp-summary-dates">
                <span class="hmp-summary-label">{{ $t('accommodation.homiii.check-in') }}</span>
                <strong :class="{ 'is-empty': startIndex === null }">
                    {{ startIndex === null ? '—' : formatIso(firstDayIso(startIndex)) }}
                </strong>
            </div>
            <span class="hmp-summary-arrow">→</span>
            <div class="hmp-summary-dates">
                <span class="hmp-summary-label">{{ $t('accommodation.homiii.check-out') }}</span>
                <strong :class="{ 'is-empty': endIndex === null }">
                    {{ endIndex === null ? '—' : formatIso(lastDayIso(endIndex)) }}
                </strong>
            </div>
            <button
                v-if="startIndex !== null"
                type="button"
                class="hmp-clear"
                @click="clearSelection">
                {{ $t('accommodation.homiii.clear') }}
            </button>
        </div>

        <!-- Navegación de años -->
        <div class="hmp-nav">
            <button
                type="button"
                class="hmp-nav-btn"
                :disabled="visibleYear <= minYear"
                @click="visibleYear -= 1">
                ‹
            </button>
            <span class="hmp-nav-year">{{ visibleYear }}</span>
            <button
                type="button"
                class="hmp-nav-btn"
                :disabled="visibleYear >= maxYear"
                @click="visibleYear += 1">
                ›
            </button>
        </div>

        <!-- Grilla de meses -->
        <div class="hmp-grid" @mouseleave="hoverIndex = null">
            <button
                v-for="cell in cells"
                :key="cell.index"
                type="button"
                class="hmp-month"
                :class="cell.classes"
                :disabled="cell.disabled"
                :title="cell.title"
                @mouseenter="hoverIndex = cell.disabled ? null : cell.index"
                @click="selectMonth(cell.index)">
                {{ cell.label }}
            </button>
        </div>

        <!-- Por qué el calendario se ve como se ve -->
        <div class="hmp-help">
            <p class="hmp-help-line">{{ phaseHint }}</p>
            <p v-if="occupiedUntil" class="hmp-help-line hmp-help-warn">
                {{ $t('accommodation.homiii.occupied-until', { date: formatIso(occupiedUntil) }) }}
            </p>
            <p v-if="minMonths > 1" class="hmp-help-line">
                {{ $t('accommodation.homiii.min-months', { months: minMonths }) }}
            </p>
            <p class="hmp-help-line hmp-help-muted">
                {{ $t('accommodation.homiii.august-rule') }}
            </p>
        </div>

        <!-- Motivo del último click rechazado -->
        <p v-if="rejectionMessage" class="hmp-rejection">{{ rejectionMessage }}</p>

    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import {
    HORIZON_MONTHS,
    monthIndex,
    fromIndex,
    indexFromDate,
    indexFromIso,
    firstDayIso,
    lastDayIso,
    firstDayDate,
    lastDayDate,
    monthCount,
    firstBookableIndex,
    validEndRange,
    isValidStart,
    validateContract,
} from '@/Utils/homiiiContract';

/**
 * Selector de meses para los alojamientos de Homiii.
 *
 * Homiii solo acepta contratos de meses completos (entrada el día 1, salida el
 * último día), así que un calendario de días dejaría el 90% de las casillas
 * muertas. Aquí se elige mes de entrada y mes de salida, y el componente escribe
 * en el store las mismas fechas 'YYYY-MM-DD' que escribiría el datepicker
 * normal: aguas abajo (Pay, Simulator, la reserva) no cambia nada.
 */
export default {
    name: 'HomiiiMonthPicker',

    props: {
        /** Duración mínima del contrato, en meses. Es POR HABITACIÓN. */
        minMonths: {
            type: Number,
            default: 1,
        },
        /** 'YYYY-MM-DD': último día ocupado según Homiii. */
        occupiedUntil: {
            type: String,
            default: null,
        },
        horizonMonths: {
            type: Number,
            default: HORIZON_MONTHS,
        },
    },

    data() {
        return {
            hoverIndex: null,
            visibleYear: new Date().getFullYear(),
            rejectionMessage: null,
        };
    },

    computed: {
        ...mapState('search', ['checkInDate', 'checkOutDate']),

        startIndex() {
            return indexFromIso(this.checkInDate);
        },

        endIndex() {
            return indexFromIso(this.checkOutDate);
        },

        firstIndex() {
            return firstBookableIndex({ occupiedUntil: this.occupiedUntil });
        },

        horizonEnd() {
            return indexFromDate(new Date()) + this.horizonMonths;
        },

        ruleOptions() {
            return {
                minMonths: this.minMonths,
                firstIndex: this.firstIndex,
                horizonEnd: this.horizonEnd,
            };
        },

        minYear() {
            return fromIndex(this.firstIndex).year;
        },

        maxYear() {
            return fromIndex(this.horizonEnd).year;
        },

        /** Rango de salidas válidas una vez elegida la entrada. */
        endRange() {
            if (this.startIndex === null) return null;
            return validEndRange(this.startIndex, this.ruleOptions);
        },

        /** Hasta dónde se pinta el rango mientras el ratón se mueve. */
        previewEnd() {
            if (this.endIndex !== null) return this.endIndex;
            if (this.startIndex === null || this.hoverIndex === null) return null;
            if (this.hoverIndex < this.startIndex) return null;
            return this.hoverIndex;
        },

        cells() {
            const cells = [];

            const choosingEnd = this.startIndex !== null && this.endIndex === null;

            for (let month0 = 0; month0 < 12; month0 += 1) {
                const index = monthIndex(this.visibleYear, month0);
                const selectable = this.isSelectable(index);
                const inRange = this.previewEnd !== null &&
                    this.startIndex !== null &&
                    index > this.startIndex &&
                    index <= this.previewEnd;

                // Eligiendo salida, casi cualquier mes vale para reempezar el
                // rango. Se pintan apagados para que se vea de un vistazo cuáles
                // son salidas válidas de verdad.
                const isEndCandidate = choosingEnd && this.isValidEndCandidate(index);
                const isRestart = choosingEnd && selectable && !isEndCandidate;

                // El mes elegido nunca se pinta como bloqueado aunque por sí solo
                // no sea una entrada válida: clickearlo deshace la selección.
                const isSelected = index === this.startIndex || index === this.endIndex;

                cells.push({
                    index,
                    label: this.monthLabel(month0),
                    disabled: !selectable && !isSelected,
                    title: this.cellTitle(index, selectable, isRestart),
                    classes: {
                        'is-disabled': !selectable && !isSelected,
                        'is-restart': isRestart,
                        'is-start': index === this.startIndex,
                        'is-end': index === this.endIndex,
                        'is-in-range': inRange,
                    },
                });
            }

            return cells;
        },

        phaseHint() {
            if (this.startIndex === null) {
                return this.$t('accommodation.homiii.pick-start');
            }
            if (this.endIndex === null) {
                return this.$t('accommodation.homiii.pick-end');
            }
            return this.$t('accommodation.homiii.selected', {
                months: monthCount(this.startIndex, this.endIndex),
            });
        },
    },

    created() {
        // Abrir el calendario en el año donde realmente se puede reservar, no en
        // el actual si el alojamiento está ocupado hasta el año que viene.
        this.visibleYear = fromIndex(this.firstIndex).year;

        // Fechas heredadas de una búsqueda anterior que no cumplen las reglas de
        // Homiii: se descartan en vez de dejar el botón de reservar activo.
        if (this.startIndex !== null || this.endIndex !== null) {
            const check = validateContract(this.startIndex, this.endIndex, this.ruleOptions);
            if (!check.ok) {
                this.clearSelection();
            }
        }
    },

    methods: {
        ...mapActions('search', ['setCheckInDate', 'setCheckOutDate']),
        ...mapActions('simulator', ['setInitDate', 'setEndDate', 'setAvailable']),

        firstDayIso,
        lastDayIso,

        monthLabel(month0) {
            const locale = (this.$i18n && this.$i18n.locale) ? this.$i18n.locale : 'es';
            return new Date(2000, month0, 1)
                .toLocaleDateString(locale, { month: 'short' })
                .replace('.', '');
        },

        formatIso(iso) {
            if (!iso) return '';
            const [year, month, day] = iso.split('-');
            return `${day}/${month}/${year}`;
        },

        /** ¿Este mes cierra un contrato válido con la entrada ya elegida? */
        isValidEndCandidate(index) {
            const range = this.endRange;
            return !!range && index >= range.from && index <= range.to;
        },

        /**
         * Un mes es clickeable si sirve como entrada, o —cuando ya hay entrada
         * elegida— si sirve como salida para esa entrada.
         */
        isSelectable(index) {
            if (this.startIndex !== null && this.endIndex === null) {
                // Siempre se puede reempezar eligiendo otra entrada.
                return this.isValidEndCandidate(index) || isValidStart(index, this.ruleOptions);
            }

            return isValidStart(index, this.ruleOptions);
        },

        cellTitle(index, selectable, isRestart) {
            if (!selectable) return this.disabledReason(index);
            if (isRestart) return this.$t('accommodation.homiii.restart');
            return '';
        },

        disabledReason(index) {
            if (index < this.firstIndex) {
                return this.occupiedUntil
                    ? this.$t('accommodation.homiii.occupied-until', { date: this.formatIso(this.occupiedUntil) })
                    : this.$t('accommodation.homiii.too-early');
            }

            // Eligiendo salida, el motivo concreto lo da la validación del rango.
            if (this.startIndex !== null && this.endIndex === null) {
                const check = validateContract(this.startIndex, index, this.ruleOptions);
                if (!check.ok) {
                    return this.reasonText(check);
                }
            }

            // Ni el mes ni el contrato mínimo que arrancaría en él caben en el
            // horizonte que se ofrece.
            if (index > this.horizonEnd || (index + this.minMonths - 1) > this.horizonEnd) {
                return this.$t('accommodation.homiii.too-far');
            }

            // No sirve como entrada: la única causa que queda es que agosto caiga
            // dentro del contrato mínimo.
            return this.$t('accommodation.homiii.august-blocks-start', { months: this.minMonths });
        },

        reasonText(check) {
            switch (check.code) {
                case 'too-short':
                    return this.$t('accommodation.homiii.min-months', { months: check.params.months });
                case 'august':
                    return this.$t('accommodation.homiii.august-limit', { date: this.formatIso(check.params.last) });
                case 'occupied':
                    // Sin ocupación conocida, el motivo es que el mes ya pasó.
                    return this.occupiedUntil
                        ? this.$t('accommodation.homiii.occupied-until', { date: this.formatIso(this.occupiedUntil) })
                        : this.$t('accommodation.homiii.too-early');
                case 'inverted':
                    return this.$t('accommodation.homiii.inverted');
                case 'out-of-horizon':
                    return this.$t('accommodation.homiii.too-far');
                default:
                    return '';
            }
        },

        selectMonth(index) {
            this.rejectionMessage = null;

            // Click sobre un extremo ya elegido: deshace la selección.
            if (this.startIndex !== null && this.endIndex !== null &&
                (index === this.startIndex || index === this.endIndex)) {
                this.clearSelection();
                return;
            }

            // Segundo click: cerrar el rango si el mes sirve de salida.
            if (this.startIndex !== null && this.endIndex === null) {
                const check = validateContract(this.startIndex, index, this.ruleOptions);
                if (check.ok) {
                    this.applySelection(this.startIndex, index);
                    return;
                }

                // No sirve de salida pero sí de entrada: se reempieza ahí.
                if (isValidStart(index, this.ruleOptions)) {
                    this.applySelection(index, null);
                    return;
                }

                this.rejectionMessage = this.reasonText(check);
                return;
            }

            // Primer click, o tercero (rango ya cerrado -> se empieza de nuevo).
            if (!isValidStart(index, this.ruleOptions)) {
                this.rejectionMessage = this.disabledReason(index);
                return;
            }

            this.applySelection(index, null);
        },

        applySelection(start, end) {
            this.setCheckInDate(start === null ? null : firstDayDate(start));
            this.setInitDate(start === null ? null : firstDayDate(start));

            this.setCheckOutDate(end === null ? null : lastDayDate(end));
            this.setEndDate(end === null ? null : lastDayDate(end));

            // El botón de reservar solo se habilita cuando Homiii confirma; con
            // el rango incompleto tiene que estar apagado.
            if (end === null) {
                this.setAvailable(false);
            }

            this.$emit('range-changed', {
                start: start === null ? null : firstDayIso(start),
                end: end === null ? null : lastDayIso(end),
                months: (start !== null && end !== null) ? monthCount(start, end) : null,
            });
        },

        clearSelection() {
            this.hoverIndex = null;
            this.rejectionMessage = null;
            this.applySelection(null, null);
        },
    },
};
</script>

<style lang="scss" scoped>
.homiii-month-picker {
    border: 1px solid $grey-10;
    border-radius: $border-radius;
    padding: 14px;
}

.hmp-summary {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    padding-bottom: 12px;
    border-bottom: 1px solid $grey-10;
}

.hmp-summary-dates {
    display: flex;
    flex-direction: column;
    min-width: 92px;

    strong {
        font-size: 0.95rem;

        &.is-empty {
            color: $grey-50;
            font-weight: 400;
        }
    }
}

.hmp-summary-label {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: $grey-50;
}

.hmp-summary-arrow {
    color: $grey-50;
    padding-bottom: 2px;
}

.hmp-clear {
    margin-left: auto;
    background: none;
    border: none;
    padding: 0;
    font-size: 0.82rem;
    color: $grey-75;
    text-decoration: underline;
    cursor: pointer;
}

.hmp-nav {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 18px;
    padding: 12px 0 8px;
}

.hmp-nav-year {
    font-size: 1rem;
    font-weight: 600;
    min-width: 56px;
    text-align: center;
}

.hmp-nav-btn {
    background: none;
    border: none;
    font-size: 1.4rem;
    line-height: 1;
    color: $grey-75;
    cursor: pointer;
    padding: 0 6px;

    &:disabled {
        color: $grey-10;
        cursor: default;
    }
}

.hmp-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
}

.hmp-month {
    padding: 12px 4px;
    border: 1px solid transparent;
    border-radius: $border-radius;
    background: $grey-5;
    font-size: 0.86rem;
    text-transform: capitalize;
    color: $grey-75;
    cursor: pointer;
    transition: background 0.12s ease, color 0.12s ease;

    &:hover:not(:disabled) {
        border-color: $hf-primary;
    }

    &.is-disabled {
        background: transparent;
        color: $grey-10;
        cursor: not-allowed;
        text-decoration: line-through;
    }

    // Clickeable, pero solo para reempezar el rango: no es una salida válida.
    &.is-restart {
        background: transparent;
        border: 1px dashed $grey-10;
        color: $grey-50;
    }

    &.is-in-range {
        background: rgba($hf-primary, 0.18);
        color: $grey-75;
    }

    &.is-start,
    &.is-end {
        background: $hf-primary;
        color: #ffffff;
        font-weight: 600;
    }
}

.hmp-help {
    padding-top: 12px;
}

.hmp-help-line {
    margin: 0 0 4px;
    font-size: 0.8rem;
    color: $grey-75;
}

.hmp-help-warn {
    color: var(--orange-text, #c25e00);
}

.hmp-help-muted {
    color: $grey-50;
}

.hmp-rejection {
    margin: 6px 0 0;
    font-size: 0.82rem;
    color: $hf-rufous;
}
</style>
